import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useAiChatDatasourceRows from '../hooks/useAiChatDatasourceRows';
import buildLineChartData from '../utils/buildLineChartData';
import AiChatChartFrame from './AiChatChartFrame';
import AiChatLineChart from './AiChatLineChart';

import type { ReactElement } from 'react';
import type IAiChatLineChartDisplay from '../interfaces/IAiChatLineChartDisplay';

/** A `showChart` call of type `line`, done: every row of the datasource read, then drawn as lines. */
function AiChatLineChartDisplay({ shown }: IAiChatLineChartDisplay): ReactElement {
  const { t } = useTranslation();
  const {
    columns, rows, executedAt, droppedReason, isLoading, isError,
  } = useAiChatDatasourceRows(shown.datasourceId, shown.rowCount);

  const data = useMemo(() => buildLineChartData(columns, rows, shown), [columns, rows, shown]);

  // The columns behind the chart, in the order a reader would look for them: x, then series, then y.
  const tableColumns = useMemo(
    () => [shown.x.column, ...(shown.seriesColumn ? [shown.seriesColumn] : []), ...shown.y.columns],
    [shown],
  );

  const note = data.droppedSeries
    ? t('aiChat.tools.chartSeriesDropped', {
      shown: data.series.length, total: data.series.length + data.droppedSeries,
    })
    : undefined;

  return (
    <AiChatChartFrame
      datasourceId={shown.datasourceId}
      title={shown.title}
      columns={tableColumns}
      rowCount={shown.rowCount}
      truncated={shown.truncated}
      isLoading={isLoading}
      isError={isError}
      executedAt={executedAt}
      droppedReason={droppedReason}
      note={note}
    >
      {data.labels.length ? (
        <AiChatLineChart data={data} xLabel={shown.x.label} yLabel={shown.y.label} />
      ) : (
        <p>{t('aiChat.tools.chartEmpty')}</p>
      )}
    </AiChatChartFrame>
  );
}

export default AiChatLineChartDisplay;
